/**
 * Session Management Utilities
 * Handles authentication token storage and user session management
 */

import type { PlatformUser } from '@/types';

export type User = PlatformUser;

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'auth_user';

/**
 * Set a cookie in the browser
 * Note: For production, consider using HTTP-only cookies set by the server
 */
function setCookie(name: string, value: string, days: number = 7): void {
  if (typeof window !== 'undefined') {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }
}

/**
 * Get a cookie from the browser
 */
function getCookie(name: string): string | null {
  if (typeof window !== 'undefined') {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
  }
  return null;
}

/**
 * Delete a cookie from the browser
 */
function deleteCookie(name: string): void {
  if (typeof window !== 'undefined') {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
}

/**
 * Store authentication token in both localStorage and cookie
 * Cookie is used by middleware for route protection
 * localStorage is used by client-side code
 */
export function setAuthToken(token: string, rememberMe: boolean = false): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TOKEN_KEY, token);
    // Set cookie with appropriate expiration
    const days = rememberMe ? 30 : 7;
    setCookie(TOKEN_KEY, token, days);
  }
}

/**
 * Get authentication token from localStorage or cookie
 */
export function getAuthToken(): string | null {
  if (typeof window !== 'undefined') {
    // Try localStorage first
    const localToken = localStorage.getItem(TOKEN_KEY);
    if (localToken) return localToken;

    // Fallback to cookie
    return getCookie(TOKEN_KEY);
  }
  return null;
}

/**
 * Store refresh token in both localStorage and cookie
 */
export function setRefreshToken(token: string, rememberMe: boolean = false): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
    const days = rememberMe ? 30 : 7;
    setCookie(REFRESH_TOKEN_KEY, token, days);
  }
}

/**
 * Get refresh token from storage
 */
export function getRefreshToken(): string | null {
  if (typeof window !== 'undefined') {
    const localToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (localToken) return localToken;
    return getCookie(REFRESH_TOKEN_KEY);
  }
  return null;
}

/**
 * Remove authentication token from both localStorage and cookie
 */
export function removeAuthToken(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    deleteCookie(TOKEN_KEY);
  }
}

/**
 * Remove refresh token from storage
 */
export function removeRefreshToken(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    deleteCookie(REFRESH_TOKEN_KEY);
  }
}

/**
 * Store user data in localStorage
 */
export function setUser(user: User): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
}

/**
 * Get user data from localStorage
 */
export function getUser(): User | null {
  if (typeof window !== 'undefined') {
    const userStr = localStorage.getItem(USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr) as User;
      } catch (e) {
        console.error('Failed to parse user data:', e);
        return null;
      }
    }
  }
  return null;
}

/**
 * Remove user data from localStorage
 */
export function removeUser(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(USER_KEY);
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/**
 * Clear all authentication data (logout)
 */
export function clearAuth(): void {
  removeAuthToken();
  removeRefreshToken();
  removeUser();
}

/**
 * Set complete auth session (token + user)
 */
export function setAuthSession(accessToken: string, refreshToken: string, user: User, rememberMe: boolean = false): void {
  console.log('[SESSION] Setting auth session:', { user: user.email, rememberMe });
  setAuthToken(accessToken, rememberMe);
  setRefreshToken(refreshToken, rememberMe);
  console.log('[SESSION] Tokens set');
  setUser(user);
  console.log('[SESSION] User set');
}
